import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Sparkles, X, ArrowLeftRight, Users } from "lucide-react";
import { api } from "@/lib/api";
import { T } from "@/lib/testIds";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import FDRPill from "@/components/app/FDRPill";
import { cn } from "@/lib/utils";

const useDebounce = (v, ms = 250) => {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
};

function PlayerSearch({ onPick, testId }) {
  const [mode, setMode] = useState("search"); // 'search' | 'squad'
  const [q, setQ] = useState("");
  const dq = useDebounce(q);
  const { data } = useQuery({
    queryKey: ["compare-search", dq],
    queryFn: async () =>
      (await api.get("/players", { params: { q: dq, limit: 6, sort: "total_points" } })).data,
    enabled: mode === "search" && dq.trim().length >= 2,
    staleTime: 60_000,
  });
  const { data: squad } = useQuery({
    queryKey: ["compare-my-squad"],
    queryFn: async () => (await api.get("/my-team")).data,
    enabled: mode === "squad",
    staleTime: 60_000,
  });

  return (
    <div className="p-3">
      {/* Mode tabs */}
      <div className="grid grid-cols-2 gap-1 mb-3 p-0.5 bg-totem-surface border border-totem-border rounded-lg">
        <button
          onClick={() => setMode("search")}
          className={cn(
            "mono text-[10px] uppercase tracking-wider py-1.5 rounded-md transition",
            mode === "search"
              ? "bg-totem-green text-totem-bg font-semibold"
              : "text-totem-textDim"
          )}
        >
          Search
        </button>
        <button
          data-testid={testId === T.compareSearchA ? "compare-my-squad-a" : "compare-my-squad-b"}
          onClick={() => setMode("squad")}
          className={cn(
            "mono text-[10px] uppercase tracking-wider py-1.5 rounded-md transition flex items-center justify-center gap-1",
            mode === "squad"
              ? "bg-totem-gold text-totem-bg font-semibold"
              : "text-totem-textDim"
          )}
        >
          <Users size={11} /> My Squad
        </button>
      </div>

      {mode === "search" && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-totem-textDim" size={14} />
            <input
              data-testid={testId}
              autoFocus
              placeholder="Search a player…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-totem-surface border border-totem-border rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-totem-green/60"
            />
          </div>
          <ul className="mt-2 max-h-64 overflow-y-auto divide-y divide-totem-borderSoft">
            {data?.results?.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => onPick(p.id)}
                  className="w-full flex items-center gap-3 py-2 px-1 hover:bg-totem-surfaceHigh rounded-md"
                >
                  <PlayerAvatar player={p} size={32} />
                  <div className="text-left flex-1 min-w-0">
                    <div className="font-semibold truncate text-sm">{p.web_name}</div>
                    <div className="mono text-[10px] uppercase text-totem-textDim">
                      {p.team_short} • {p.position} • £{p.now_cost.toFixed(1)}m
                    </div>
                  </div>
                  <div className="mono text-totem-green text-sm">{p.total_points}</div>
                </button>
              </li>
            )) || null}
            {dq.trim().length >= 2 && data && data.count === 0 && (
              <li className="py-3 text-center text-xs text-totem-textDim">No matches.</li>
            )}
            {dq.trim().length < 2 && (
              <li className="py-3 text-center text-xs text-totem-textFaint mono uppercase">
                Type at least 2 letters
              </li>
            )}
          </ul>
        </>
      )}

      {mode === "squad" && (
        <SquadPicker squad={squad} onPick={onPick} />
      )}
    </div>
  );
}

const SquadRow = ({ p, kind, onPick }) => (
  <li>
    <button
      onClick={() => onPick(p.id)}
      className="w-full flex items-center gap-2 py-1.5 px-1 hover:bg-totem-surfaceHigh rounded-md"
    >
      <PlayerAvatar player={p} size={28} showRing={p.is_captain} />
      <div className="text-left flex-1 min-w-0">
        <div className="text-sm truncate flex items-center gap-1">
          <span className="font-semibold">{p.web_name}</span>
          {p.is_captain && <span className="mono text-[9px] px-1 bg-totem-gold/20 text-totem-gold rounded">C</span>}
          {p.is_vice_captain && !p.is_captain && (
            <span className="mono text-[9px] px-1 bg-totem-surfaceHigh text-totem-textDim rounded">V</span>
          )}
        </div>
        <div className="mono text-[9px] uppercase text-totem-textDim">
          {p.team_short} • {p.position}
        </div>
      </div>
      <span
        className={cn(
          "mono text-[9px] uppercase px-1.5 py-0.5 rounded",
          kind === "start" ? "bg-totem-green/15 text-totem-green" : "bg-totem-surfaceHigh text-totem-textDim"
        )}
      >
        {kind === "start" ? "XI" : "Sub"}
      </span>
    </button>
  </li>
);

function SquadPicker({ squad, onPick }) {
  if (!squad) {
    return (
      <div className="py-6 text-center text-xs mono uppercase text-totem-textFaint animate-pulse">
        Loading squad…
      </div>
    );
  }
  if (squad.message) {
    return (
      <div className="py-4 text-center text-xs text-totem-textDim">
        {squad.message}
        <div className="mt-1 text-[10px] mono uppercase text-totem-textFaint">
          Squad picker unlocks once GW1 kicks off.
        </div>
      </div>
    );
  }
  const starters = squad.starters || [];
  const bench = squad.bench || [];
  return (
    <div>
      {starters.length === 0 && bench.length === 0 && (
        <div className="py-4 text-center text-xs text-totem-textDim">No squad loaded.</div>
      )}
      {starters.length > 0 && (
        <>
          <div className="label-caps px-1 pt-1 pb-1">Starting XI</div>
          <ul className="divide-y divide-totem-borderSoft max-h-40 overflow-y-auto">
            {starters.map((p) => <SquadRow key={p.id} p={p} kind="start" onPick={onPick} />)}
          </ul>
        </>
      )}
      {bench.length > 0 && (
        <>
          <div className="label-caps px-1 pt-2 pb-1">Bench</div>
          <ul className="divide-y divide-totem-borderSoft max-h-32 overflow-y-auto">
            {bench.map((p) => <SquadRow key={p.id} p={p} kind="bench" onPick={onPick} />)}
          </ul>
        </>
      )}
    </div>
  );
}

function PlayerSlot({ side, playerId, onClear, onPick, testId }) {
  const [showSearch, setShowSearch] = useState(!playerId);
  useEffect(() => {
    if (playerId) setShowSearch(false);
  }, [playerId]);

  const { data: picked } = useQuery({
    queryKey: ["compare-picked", playerId],
    queryFn: async () => (await api.get(`/players/${playerId}`)).data.player,
    enabled: !!playerId,
    staleTime: 60_000,
  });

  if (!playerId || showSearch) {
    return (
      <div className="totem-card min-h-[220px]" data-testid={testId}>
        <div className="p-3 flex items-center justify-between border-b border-totem-borderSoft">
          <span className="label-caps">Player {side}</span>
          {playerId && (
            <button onClick={() => setShowSearch(false)} className="text-totem-textDim">
              <X size={14} />
            </button>
          )}
        </div>
        <PlayerSearch
          onPick={(id) => onPick(id)}
          testId={side === "A" ? T.compareSearchA : T.compareSearchB}
        />
      </div>
    );
  }

  return (
    <div className="totem-card p-3 min-h-[220px] flex flex-col items-center" data-testid={testId}>
      <div className="w-full flex items-center justify-between">
        <span className="label-caps">Player {side}</span>
        <button
          onClick={() => onClear()}
          className="text-totem-textDim hover:text-totem-red"
          title="Change"
        >
          <X size={14} />
        </button>
      </div>
      {picked ? (
        <>
          <PlayerAvatar player={picked} size={64} showRing className="mt-2" />
          <div className="mt-2 text-center">
            <div className="display-heading text-lg leading-tight">{picked.web_name}</div>
            <div className="mono text-[10px] uppercase text-totem-textDim mt-1">
              {picked.team_short} • {picked.position} • £{picked.now_cost.toFixed(1)}m
            </div>
          </div>
          <button
            onClick={() => setShowSearch(true)}
            className="mt-3 mono text-[10px] uppercase tracking-wider text-totem-textDim hover:text-totem-green"
          >
            Change
          </button>
        </>
      ) : (
        <div className="mt-6 mono text-[10px] uppercase text-totem-textFaint animate-pulse">
          Loading…
        </div>
      )}
    </div>
  );
}

const StatRow = ({ label, aValue, bValue, formatter = (v) => v, lowerBetter = false, highlight = false }) => {
  const na = typeof aValue === "number" ? aValue : Number(aValue) || 0;
  const nb = typeof bValue === "number" ? bValue : Number(bValue) || 0;
  const aWins = lowerBetter ? na < nb : na > nb;
  const bWins = lowerBetter ? nb < na : nb > na;
  const tie = na === nb;
  const max = Math.max(Math.abs(na), Math.abs(nb), 0.001);
  const aPct = Math.min(100, (Math.abs(na) / max) * 100);
  const bPct = Math.min(100, (Math.abs(nb) / max) * 100);

  const barCls = (w, l) =>
    tie
      ? "bg-totem-textDim/40"
      : w
      ? "bg-totem-green"
      : l
      ? "bg-totem-gold/70"
      : "bg-totem-textDim/40";

  return (
    <div
      data-testid={T.compareStatRow}
      className={cn("py-2", highlight && "totem-card-inner p-3 my-2 border-totem-gold/40")}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5 mono text-xs uppercase tracking-wider">
        <span className={cn(aWins ? "text-totem-green" : "text-totem-text")}>{formatter(na)}</span>
        <span className="text-totem-textDim">{label}</span>
        <span className={cn(bWins ? "text-totem-green" : "text-totem-text")}>{formatter(nb)}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="h-1.5 rounded-full bg-totem-surfaceHigh overflow-hidden flex justify-end">
          <div
            className={cn("h-full rounded-full transition-all", barCls(aWins, bWins))}
            style={{ width: `${aPct}%` }}
          />
        </div>
        <div className="h-1.5 rounded-full bg-totem-surfaceHigh overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all", barCls(bWins, aWins))}
            style={{ width: `${bPct}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default function Compare() {
  const [sp, setSp] = useSearchParams();
  const aId = sp.get("a") ? Number(sp.get("a")) : null;
  const bId = sp.get("b") ? Number(sp.get("b")) : null;

  const setSide = (side, id) => {
    const next = new URLSearchParams(sp);
    if (id) next.set(side, String(id));
    else next.delete(side);
    setSp(next, { replace: true });
  };

  const enabled = !!aId && !!bId;
  const { data, isLoading, error } = useQuery({
    queryKey: ["compare", aId, bId],
    queryFn: async () =>
      (await api.get("/compare", { params: { a: aId, b: bId } })).data,
    enabled,
    staleTime: 60_000,
  });

  return (
    <div data-testid={T.comparePage} className="space-y-4 pt-1 animate-fade-up">
      <div className="flex items-center gap-2">
        <ArrowLeftRight className="text-totem-green" size={18} />
        <h2 className="display-heading text-lg">Player vs Player</h2>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PlayerSlot
          side="A"
          playerId={aId}
          onPick={(id) => setSide("a", id)}
          onClear={() => setSide("a", null)}
          testId={T.compareSlotA}
        />
        <PlayerSlot
          side="B"
          playerId={bId}
          onPick={(id) => setSide("b", id)}
          onClear={() => setSide("b", null)}
          testId={T.compareSlotB}
        />
      </div>

      {enabled && isLoading && (
        <div className="totem-card h-72 animate-pulse" />
      )}

      {enabled && error && (
        <div className="totem-card p-4 text-totem-red text-sm">
          {String(error?.response?.data?.detail || error.message)}
        </div>
      )}

      {enabled && data && (
        <>
          {/* Stat comparison rows */}
          <div className="totem-card p-4">
            <div className="label-caps mb-3">Head-to-head stats</div>
            <StatRow label="Total Pts" aValue={data.a.player.total_points} bValue={data.b.player.total_points} />
            <StatRow label="Form" aValue={data.a.player.form} bValue={data.b.player.form} />
            <StatRow label="Points/Game" aValue={data.a.player.points_per_game} bValue={data.b.player.points_per_game} />
            <StatRow label="Expected Goals (xG)" aValue={data.a.player.expected_goals} bValue={data.b.player.expected_goals} formatter={(v) => v.toFixed(2)} />
            <StatRow label="Expected Assists (xA)" aValue={data.a.player.expected_assists} bValue={data.b.player.expected_assists} formatter={(v) => v.toFixed(2)} />
            <StatRow label="ICT Index" aValue={data.a.player.ict_index} bValue={data.b.player.ict_index} formatter={(v) => v.toFixed(1)} />
            <StatRow label="Selected By %" aValue={data.a.player.selected_by_percent} bValue={data.b.player.selected_by_percent} formatter={(v) => `${v.toFixed(1)}%`} />
            <StatRow label="Price (£m)" aValue={data.a.player.now_cost} bValue={data.b.player.now_cost} formatter={(v) => v.toFixed(1)} lowerBetter />
            <StatRow
              label="Fixture Diff. (next 3)"
              aValue={data.a.avg_fdr_next3 ?? 3}
              bValue={data.b.avg_fdr_next3 ?? 3}
              formatter={(v) => v.toFixed(1)}
              lowerBetter
            />
          </div>

          {/* Predicted points card — the marquee section */}
          <div className="totem-card p-4 border-totem-gold/30" style={{ boxShadow: "0 0 16px rgba(227,195,43,0.12)" }}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="text-totem-gold" size={14} />
              <span className="mono uppercase text-[11px] tracking-widest text-totem-gold font-semibold">
                Predicted next {data.weeks} GW
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="text-center">
                <div
                  data-testid={T.comparePredictedTotalA}
                  className="display-heading text-3xl text-totem-green"
                >
                  {data.a.predicted_total.toFixed(1)}
                </div>
                <div className="mono text-[10px] uppercase text-totem-textDim mt-1">
                  {data.a.player.web_name}
                </div>
              </div>
              <div className="text-center">
                <div
                  data-testid={T.comparePredictedTotalB}
                  className="display-heading text-3xl text-totem-green"
                >
                  {data.b.predicted_total.toFixed(1)}
                </div>
                <div className="mono text-[10px] uppercase text-totem-textDim mt-1">
                  {data.b.player.web_name}
                </div>
              </div>
            </div>

            <PredictedTable a={data.a} b={data.b} />

            <p className="mt-3 text-[11px] text-totem-textDim leading-relaxed">
              Predicted points are our v1 model: FPL&apos;s <span className="mono">ep_next</span> (or PPG /
              form fallback) weighted by opponent difficulty, home/away and minutes reliability.
              Directional guidance — not a guarantee.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function PredictedTable({ a, b }) {
  const gws = Array.from(
    new Set([...a.predicted.map((p) => p.gw), ...b.predicted.map((p) => p.gw)])
  )
    .filter(Boolean)
    .sort((x, y) => x - y);
  const rowFor = (side, gw) => side.predicted.find((p) => p.gw === gw);

  return (
    <div className="mt-2 border border-totem-borderSoft rounded-lg overflow-hidden">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-totem-surface">
            <th className="px-2 py-2 label-caps text-left">GW</th>
            <th className="px-2 py-2 label-caps text-center">{a.player.web_name}</th>
            <th className="px-2 py-2 label-caps text-center">{b.player.web_name}</th>
          </tr>
        </thead>
        <tbody>
          {gws.map((gw) => {
            const ra = rowFor(a, gw);
            const rb = rowFor(b, gw);
            return (
              <tr key={gw} className="border-t border-totem-borderSoft">
                <td className="px-2 py-2 mono text-totem-textDim">GW{gw}</td>
                <td className="px-2 py-2">
                  {ra ? (
                    <div className="flex items-center justify-center gap-2">
                      <FDRPill difficulty={ra.difficulty} label={`${ra.opponent_short || "?"}${ra.is_home ? "(H)" : "(A)"}`} />
                      <span className="mono font-semibold text-totem-green">{ra.predicted_points}</span>
                    </div>
                  ) : (
                    <span className="mono text-totem-textFaint">—</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  {rb ? (
                    <div className="flex items-center justify-center gap-2">
                      <FDRPill difficulty={rb.difficulty} label={`${rb.opponent_short || "?"}${rb.is_home ? "(H)" : "(A)"}`} />
                      <span className="mono font-semibold text-totem-green">{rb.predicted_points}</span>
                    </div>
                  ) : (
                    <span className="mono text-totem-textFaint">—</span>
                  )}
                </td>
              </tr>
            );
          })}
          {gws.length === 0 && (
            <tr>
              <td colSpan={3} className="py-4 text-center text-totem-textDim text-xs">
                No upcoming fixtures available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
